import React from 'react';
import { supabase } from '../../../lib/supabase';
import { cn } from '../../../utils/cn';
import { DocumentIcon } from './DocumentIcon';
import { ImportantBadge } from './ImportantBadge';

interface DocumentLinkProps {
  name: string;
  link: string;
  isImportant: boolean;
  underlineOnHoverOnly?: boolean;
  showBadge?: boolean;
  forceLightText?: boolean;
}

export function DocumentLink({ name, link, isImportant, underlineOnHoverOnly = false, showBadge = true, forceLightText = false }: DocumentLinkProps) {
  const textClass = forceLightText
    ? 'text-condo-light group-hover:text-condo-dark'
    : (isImportant ? 'text-condo-light group-hover:text-condo-dark' : 'text-condo-dark group-hover:text-condo-dark');

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!link) return;

    try {
      // Bestem bucket-navn - for "Viktige dokumenter" er det alltid residence-documents
      const bucketName = 'residence-documents';
      
      // Hvis link allerede er en full URL, ekstraher filstien fra den
      // Ellers bruk link direkte som filsti
      let filePath: string;
      
      if (link.startsWith('http')) {
        // Ekstraher filstien fra URL-en
        // URL format: https://...supabase.co/storage/v1/object/public/residence-documents/{filePath}
        const urlParts = link.split('/storage/v1/object/public/residence-documents/');
        if (urlParts.length > 1) {
          filePath = urlParts[1];
        } else {
          // Hvis vi ikke kan ekstrahere filstien, prøv å bruke URL-en direkte
          window.open(link, '_blank', 'noopener,noreferrer');
          return;
        }
      } else {
        // Link er allerede en filsti
        filePath = link;
      }

      // Last ned filen direkte fra Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .download(filePath);

      if (error) {
        console.error('Error downloading file:', error);
        // Fallback: prøv å åpne URL-en direkte hvis download feiler
        if (link.startsWith('http')) {
          window.open(link, '_blank', 'noopener,noreferrer');
        } else {
          alert('Kunne ikke åpne filen. Vennligst prøv igjen senere.');
        }
        return;
      }

      if (!data) {
        console.error('No data returned from download');
        alert('Kunne ikke åpne filen. Vennligst prøv igjen senere.');
        return;
      }

      // Konverter blob til URL og åpne i ny fane
      const url = URL.createObjectURL(data);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening file:', err);
      // Fallback: prøv å åpne URL-en direkte hvis download feiler
      if (link.startsWith('http')) {
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        alert('Kunne ikke åpne filen. Vennligst prøv igjen senere.');
      }
    }
  };

  return (
    <div
      className="flex items-center gap-3 flex-1 cursor-pointer"
      onClick={handleClick}
    >
      <DocumentIcon isImportant={isImportant} />
      <span className={cn(
        textClass,
        underlineOnHoverOnly ? 'group-hover:underline' : 'underline group-hover:underline',
        isImportant && 'font-medium'
      )}>
        {name}
      </span>
      {isImportant && showBadge && <ImportantBadge />}
    </div>
  );
}